import { FC, useEffect, useRef, useState } from 'react';
import {
  StyledArrowIconButton,
  StyledBottomContainer,
  StyledBottomInnerContainer,
  StyledContainer,
  StyledLegalInfoLink,
  StyledLink,
  StyledNavLink,
  StyledNavLinksContainer,
  StyledTitle,
  StyledTopContainer,
  StyledTopFirstContainer,
  StyledTypography,
} from './Footer.styled';
import { Box } from '../Box';
import { Typography } from '../Typography';
import { legalInfoLinks, navigationItems } from './config';
import { ReactComponent as Logo } from 'assets/light-logo.svg';
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow.svg';
import { COLORS } from 'theme/colors';
import { useLocation } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';
import { AppPaths } from '../../../urls/frontend';
import { TrackGoogleAnalyticsEvent } from 'utils/google-analytics';
import { FooterLinksCategory } from 'utils/google-analytics/events/footer';
import { useWindowSize } from 'hooks/useWindowSize';

export const Footer: FC = () => {
  const { pathname } = useLocation();
  const { isMobile } = useWindowSize();
  const footerRef = useRef<HTMLElement | null>(null);
  const isHomePage = pathname === AppPaths.main;
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const currentYear = new Date().getFullYear();

  const onLinkClicked = (link: string) => {
    TrackGoogleAnalyticsEvent(
      FooterLinksCategory,
      link,
      window.location.pathname,
    );
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (footerRef.current) {
        const footerTop = footerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        setShowScrollToTop(footerTop <= windowHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <StyledContainer ref={footerRef}>
      {isMobile && showScrollToTop && (
        <StyledArrowIconButton onClick={scrollToTop}>
          <ArrowIcon
            stroke={COLORS.background.secondary}
            style={{ transform: 'rotate(-135deg)' }}
          />
        </StyledArrowIconButton>
      )}
      <StyledTopContainer>
        <StyledTopFirstContainer display="flex" flexDirection="column">
          {isHomePage ? (
            <Logo />
          ) : (
            <Link smooth to={AppPaths.main}>
              <Logo />
            </Link>
          )}
          <StyledTitle variant="body" color={COLORS.background.secondary}>
            Mehrwert durch unabhängige Fondsanalyse
          </StyledTitle>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body" color={COLORS.background.secondary}>
              Seidlstr. 26, 80335 München
            </Typography>
            <StyledLink href="tel:+4989244180230">
              +49 (89) 24 41 80230
            </StyledLink>
            <StyledLink href="fax:+4989244180249">
              Fax +49 (89) 24 41 802 49
            </StyledLink>
            <StyledLink href="mailto:research@fondsconsult.de">
              research@fondsconsult.de
            </StyledLink>
          </Box>
        </StyledTopFirstContainer>
        <StyledNavLinksContainer>
          {navigationItems.map((item) => (
            <Box>
              <Typography
                variant="overline"
                color={COLORS.background.secondary}
                weight="bold"
              >
                {item.categoryName}
              </Typography>
              <Box pt={3} display="flex" flexDirection="column" gap={2}>
                {item.items.map((link) => (
                  <StyledNavLink
                    smooth
                    to={link.link}
                    onClick={() => onLinkClicked(link.label)}
                  >
                    {link.label}
                  </StyledNavLink>
                ))}
              </Box>
            </Box>
          ))}
        </StyledNavLinksContainer>
      </StyledTopContainer>
      <StyledBottomContainer display="flex" justifyContent="space-between">
        <StyledTypography variant="body">
          &copy; {currentYear} FondConsult Research GmbH
        </StyledTypography>
        <StyledBottomInnerContainer display="flex" gap={3}>
          {legalInfoLinks.map((link) => (
            <StyledLegalInfoLink to={link.path}>
              {link.label}
            </StyledLegalInfoLink>
          ))}
        </StyledBottomInnerContainer>
      </StyledBottomContainer>
    </StyledContainer>
  );
};
